import { FamilyPortfolioReport } from "@/modules/reports/pages/family-portfolio";
import { createFileRoute } from "@tanstack/react-router";
import {
  distributorEmployeeByIdQueryOptions,
  familyDetailsQueryOptions,
  generateReportData,
  getDistributorBrandingOptions,
  getDistributorProfileOptions,
  getUserOptions,
  portfolioCategorySummaryOptions,
} from "creso-core/hooks";
import {
  buildFamilyProductBreakdownItems,
  findBreakdownByCategory,
  getAssetAllocationBucketsFromBreakdown,
} from "creso-core/utils";
import { PortfolioBreakdown } from "creso-core/types";
import { EPOCH_DATE } from "creso-core/constants";
import { portfolioSourceSchema } from "creso-core/zod";
import { format } from "date-fns";
import { boolean, object, string } from "zod";
import { ASSET_ALLOCATION_BUCKET_COLORS } from "@crm/components/overview/asset-allocation";

export const Route = createFileRoute(
  "/(auth)/(headless)/reports/families/$id/portfolio"
)({
  component: FamilyPortfolioReport,
  validateSearch: object({
    source: portfolioSourceSchema.optional(),
    date_range: string().optional(),
    start_date: string().optional(),
    end_date: string().optional(),
    include_transactions: boolean().optional(),
  }),
  loaderDeps: ({ search }) => ({
    source: search.source,
    date_range: search.date_range,
    start_date: search.start_date,
    end_date: search.end_date,
    include_transactions: search.include_transactions,
  }),
  loader: async ({ context, params, deps }) => {
    const { queryClient } = context;
    const userPromise = queryClient.fetchQuery(getUserOptions(true));
    const familyPromise = queryClient.fetchQuery(
      familyDetailsQueryOptions(Number(params.id))
    );
    const distributorProfilePromise = queryClient.fetchQuery(
      getDistributorProfileOptions(true)
    );
    const distributorBrandingPromise = queryClient.fetchQuery(
      getDistributorBrandingOptions(true)
    );

    const {
      source = "OVERALL",
      date_range,
      start_date,
      end_date,
      include_transactions,
    } = deps;

    const resolvedDateRange = date_range ?? "till_today";
    const reportType =
      resolvedDateRange === "till_today" ? "holdings" : "statement";
    const user = await userPromise;
    const metadata =
      user?.metadata && typeof user.metadata === "object"
        ? (user.metadata as Record<string, unknown>)
        : null;
    const rawEmployeeId = metadata?.id ?? null;
    const employeeId =
      user?.user_type === "employee"
        ? typeof rawEmployeeId === "number"
          ? rawEmployeeId
          : typeof rawEmployeeId === "string"
            ? Number(rawEmployeeId)
            : undefined
        : undefined;

    const employeePromise =
      employeeId != null && user?.user_type === "employee"
        ? queryClient.fetchQuery(
            distributorEmployeeByIdQueryOptions(employeeId, { enabled: true })
          )
        : Promise.resolve(null);

    const family = await familyPromise;

    if (!family) {
      throw new Error("Family not found");
    }

    const portfolioJson = await generateReportData<
      "holdings" | "statement",
      "json"
    >({
      file_type: "json",
      type: reportType,
      family_id: family.id,
      source,
      start_date: start_date ?? EPOCH_DATE,
      end_date: end_date ?? format(new Date(), "yyyy-MM-dd"),
      additional_details: {
        include_transactions: false,
      },
    }).catch(() => null);

    const holdings = portfolioJson?.holdings ?? [];
    const memberSummary = portfolioJson?.member_summary ?? [];
    const sips = portfolioJson?.ongoing_xsip ?? [];

    const ordersJson = include_transactions
      ? await generateReportData<"recent_transactions", "json">({
          file_type: "json",
          type: "recent_transactions",
          family_id: family.id,
          start_date:
            date_range === "till_today" ? EPOCH_DATE : (start_date ?? ""),
          end_date: end_date ?? format(new Date(), "yyyy-MM-dd"),
          additional_details: {
            order_status: "COMPLETED",
          },
        }).catch(() => null)
      : null;

    const transactions = ordersJson?.transactions ?? [];

    const distributorProfile = await distributorProfilePromise;

    const portfolioBreakdown = await queryClient
      .fetchQuery(portfolioCategorySummaryOptions({ familyId: family.id }))
      .catch(() => [] as PortfolioBreakdown[]);
    const breakdownBySource = findBreakdownByCategory(
      portfolioBreakdown,
      source
    );
    const productBreakdownItems = buildFamilyProductBreakdownItems(
      breakdownBySource,
      distributorProfile
    );
    const allocationBuckets = getAssetAllocationBucketsFromBreakdown(
      breakdownBySource,
      ASSET_ALLOCATION_BUCKET_COLORS
    );

    return {
      family,
      distributorProfile,
      distributorBranding: await distributorBrandingPromise,
      employee: await employeePromise,
      summary: portfolioJson?.summary ?? undefined,
      holdings,
      memberSummary,
      sips,
      transactions,
      allocationBuckets,
      productBreakdownItems,
    };
  },
});
